import {
  RLearningJourneyTeamImportFileErrorCode,
  useLearningJourneyConfirmTeamImportMutationRemote,
  useLearningJourneyPreviewTeamImportMutationRemote,
} from '@/store/v2';
import { LearningJourneyTeamImportConfirmation, LearningJourneyTeamImportPreviewResult } from '@/store/v2/journeys/teamImport';

/**
 * Stands in for a missing or unreadable `errorCause`. The server should always name a cause when
 * it refuses a file, but the schema allows it to be null, and a transport failure gives us nothing
 * at all — both leave the admin with the same "try again" message.
 */
const FALLBACK_FILE_ERROR_CODE = RLearningJourneyTeamImportFileErrorCode.InvalidCsv;

/**
 * The two calls of the populate-learning-teams-from-CSV flow, for one journey.
 *
 * Both take the file itself rather than an id: preview hands back no token, so confirming
 * re-uploads and re-parses from scratch. Unlike the two bulk-invite flows, confirm is synchronous
 * and wipe-and-replace — it applies the teams and answers with what it did, so a retry after a
 * timeout would rebuild the teams rather than duplicate them, but callers must still keep the
 * button disabled for the whole in-flight window.
 */
export const useLearningJourneyTeamImport = (
  journeyId: number,
): {
  previewImport: (file: File) => Promise<LearningJourneyTeamImportPreviewResult>;
  previewLoading: boolean;
  /** Resolves to `undefined` when the teams were not applied. */
  confirmImport: (file: File) => Promise<LearningJourneyTeamImportConfirmation | undefined>;
  confirmLoading: boolean;
} => {
  const [previewTeamImport, { loading: previewLoading }] = useLearningJourneyPreviewTeamImportMutationRemote();
  const [confirmTeamImport, { loading: confirmLoading }] = useLearningJourneyConfirmTeamImportMutationRemote();

  return {
    previewLoading,
    confirmLoading,

    previewImport: async (file: File) => {
      const { data } = await previewTeamImport({ variables: { input: { file, journeyId } } });
      const result = data?.learningJourneyPreviewTeamImport;

      if (!result?.success || !result.data) {
        return { errorCode: result?.errorCause?.code || FALLBACK_FILE_ERROR_CODE };
      }

      const { allJourneyLearnersAssigned, stats, rows, missingLearners } = result.data;

      return {
        preview: {
          allJourneyLearnersAssigned,
          stats,
          rows: rows.map(({ outcome, row }) => ({
            outcome,
            email: row.email,
            teamName: row.teamName,
            sourceLine: row.sourceLine,
          })),
          missingLearners: missingLearners.map(({ email, fullName }) => ({ email, fullName })),
        },
      };
    },

    confirmImport: async (file: File) => {
      const { data } = await confirmTeamImport({
        variables: { input: { file, journeyId } },
        // The journey is the only thing holding the teams, and it is already in the cache: the
        // Discussion channels page the admin came from read it. Without this, the Learning Teams
        // page they land on next serves that copy and shows no teams at all.
        update: (cache, { data: result }) => {
          if (result?.learningJourneyConfirmTeamImport?.success) {
            cache.evict({ fieldName: 'learningJourney' });
          }
        },
      });
      const result = data?.learningJourneyConfirmTeamImport;

      // A `false` is either a refusal that changed nothing — the server re-checks coverage and
      // declines before deleting anything — or a failure partway through the wipe-and-replace,
      // which can leave the journey with no teams. The flow cannot tell them apart, so it sends
      // the admin back to Discussion channels, which is where both states are recoverable from.
      return result?.success ? result : undefined;
    },
  };
};
